import React, { useEffect, useState } from 'react';
import api from '../api';
import RunsPicture, { MovedList } from './RunsPicture';

// A run left a gap on its machine -- it finished early, or came off -- and
// the order page, where the board is out of sight, asks what to do with it,
// drawn (the director, 15 Oct 2026): bring the runs behind forward, or leave
// them where they are. The same Close the gap as the board's.
//
// offer: { machine_id, from, to, keep_ids, says } as the server offers it.

const TEAL   = '#14b8a6';
const AMBER  = '#f59e0b';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const DIM    = '#9ca3af';

export default function GapModal({ offer, heading, toast, onClose, onDone }) {
  const [lane, setLane] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [choice, setChoice] = useState('forward');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.post('/api/v1/schedule/close-gap/preview', {
      machine_id: offer.machine_id, from: offer.from, to: offer.to, keep_ids: offer.keep_ids || [],
    })
      .then(r => { setLane(r.data?.lane || null); if (!r.data?.lane) onClose(); })
      .catch(err => setError(err.response?.data?.error || 'Could not draw the gap.'))
      .finally(() => setLoading(false));
  }, [offer]); // eslint-disable-line react-hooks/exhaustive-deps

  function bringForward() {
    setSaving(true);
    api.post('/api/v1/schedule/close-gap', {
      machine_id: offer.machine_id, from: offer.from, to: offer.to, keep_ids: offer.keep_ids || [],
    })
      .then(r => {
        toast.success(`Brought forward — ${r.data.moved} run${r.data.moved === 1 ? '' : 's'} moved up on ${lane?.machine || 'the machine'}.`, 7000);
        onDone();
      })
      .catch(err => { setError(err.response?.data?.error || 'Could not bring them forward.'); setSaving(false); });
  }

  if (loading || !lane) {
    return error ? (
      <Shell onClose={onClose} heading={heading}>
        <div style={{ color: '#fca5a5', fontSize: 12.5 }}>{error}</div>
      </Shell>
    ) : null;
  }

  return (
    <Shell onClose={onClose} heading={heading}>
      <div style={{ fontSize: 13, color: '#d1d5db', marginBottom: 12 }}>
        It left <strong>{lane.machine}</strong> free from {fmt(offer.from)} to {fmt(offer.to)}. Bring the runs behind it forward, or leave them where they are?
      </div>
      <RunsPicture lane={lane} choice={choice} />
      {error && <div style={{ color: '#fca5a5', fontSize: 12.5, marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <Toggle on={choice === 'forward'} onClick={() => setChoice('forward')} tone={AMBER}>Show brought forward</Toggle>
        <Toggle on={choice === 'leave'} onClick={() => setChoice('leave')} tone={TEAL}>Show left where they are</Toggle>
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={{ background: 'none', border: '1px solid #374151', borderRadius: 8, color: DIM, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>
          Leave them where they are
        </button>
        <button onClick={bringForward} disabled={saving}
          style={{ background: `${AMBER}26`, border: `1px solid ${AMBER}88`, borderRadius: 8, color: AMBER, padding: '8px 18px', cursor: saving ? 'default' : 'pointer', fontSize: 13, fontWeight: 600 }}>
          {saving ? 'Moving…' : 'Bring forward'}
        </button>
      </div>
      <div style={{ fontSize: 11.5, color: MUTED, marginTop: 8 }}><MovedList lane={lane} /></div>
    </Shell>
  );
}

const fmt = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

function Shell({ heading, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label={heading}
        style={{ background: '#1a2030', border: `1px solid ${BORDER}`, borderRadius: 16, width: '100%', maxWidth: 760, padding: '18px 20px', fontFamily: 'system-ui' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f2f5' }}>{heading}</div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Toggle({ on, onClick, tone, children }) {
  return (
    <button onClick={onClick}
      style={{ background: on ? `${tone}1f` : 'none', border: `1px solid ${on ? `${tone}88` : BORDER}`, color: on ? tone : DIM,
               borderRadius: 999, padding: '4px 11px', fontSize: 11.5, cursor: 'pointer' }}>
      {children}
    </button>
  );
}
